import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { api, errorMessage } from "@/lib/api";
import { PageHero, SectionHeader, CtaBand, ServiceAreasGrid, PriceTable, FaqList, BookNowButton, useCatalogParts } from "@/components/marketing/Sections";

export function PostalCheck() {
  const [pc, setPc] = useState("");
  const [result, setResult] = useState(null);
  const check = async (e) => {
    e.preventDefault();
    try { const r = await api.get("/public/service-areas/check", { params: { postal_code: pc } }); setResult(r.data); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <form onSubmit={check} className="kc-card p-6 sm:p-8" data-testid="postal-check-form">
      <label className="kc-label" htmlFor="pc">Check your postal code</label>
      <div className="flex gap-2"><input id="pc" className="kc-input uppercase" placeholder="e.g. L6P 1A1" value={pc} onChange={(e) => setPc(e.target.value)} data-testid="postal-check-input" required />
        <button className="btn-primary h-12 px-5" data-testid="postal-check-button"><Search className="w-4 h-4" /> Check</button></div>
      {result && <p className={`mt-4 text-sm font-medium ${result.eligible ? "text-[#067647]" : "text-[#B42318]"}`} role="status" data-testid="postal-check-result">{result.message}</p>}
    </form>
  );
}

export function ServiceAreas() {
  const { areas, settings } = useCatalogParts();
  useSeo("Service Areas — Brampton, Mississauga, Milton, Caledon, Toronto & Oakville", "Kaur Cleaning Services provides residential cleaning across the GTA. See every city we serve and check your postal code.");
  return (
    <div>
      <PageHero eyebrow="Where we clean" title={settings?.service_region_label || "Serving the GTA & Surrounding Areas"} intro="Local cleaners, local knowledge. Enter your postal code to confirm we serve your home." />
      <section className="kc-section">
        <div className="kc-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8" data-testid="service-areas-grid"><ServiceAreasGrid areas={areas} /></div>
          <div className="lg:col-span-4"><PostalCheck /></div>
        </div>
      </section>
      <CtaBand source="service-areas" />
    </div>
  );
}

export function ServiceAreaDetail() {
  const { slug } = useParams();
  const { areas, catalog, standardGeneral, deepGeneral, faqs, settings, services } = useCatalogParts();
  const area = areas.find((a) => a.slug === slug);
  useSeo(area ? `House & Condo Cleaning in ${area.city}, ${area.province}` : "Service Area", area ? `${area.description} Book professional standard or deep cleaning in ${area.city} online with upfront pricing.` : "");
  if (catalog && !area) return <Navigate to="/service-areas" replace />;
  if (!area) return null;
  const others = areas.filter((a) => a.slug !== slug);
  return (
    <div data-testid={`service-area-page-${slug}`}>
      <PageHero eyebrow={`Serving ${area.city}, ${area.province}`} title={`Home cleaning in ${area.city}`} intro={area.description}
        image={{ src: "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: `Clean kitchen in a ${area.city} home` }}>
        <div className="flex flex-col sm:flex-row gap-3"><BookNowButton source={`area-${slug}`} label={`BOOK IN ${area.city.toUpperCase()}`} testId="area-book-now" /><Link to="/pricing" className="btn-secondary">View pricing</Link></div>
      </PageHero>

      <section className="kc-section">
        <div className="kc-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Local service" title={`What ${area.city} homeowners get`} />
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {[`${services.map((s) => s.name).join(" and ")} for houses, townhouses, condos and apartments`, "Upfront, itemized pricing before you confirm", "Live booking status from “Received” to “Completed”", "Cleaners familiar with " + area.city + " neighbourhoods", ...(area.within_radius ? ["Inside our standard service radius — no distance adjustment"] : ["Extended service area — a small distance adjustment is shown in your quote"])].map((x) => (
                <li key={x} className="flex gap-2 text-sm rounded-xl bg-white border border-[#E5E2DA] px-4 py-3"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[var(--kc-blue)] shrink-0" />{x}</li>
              ))}
            </ul>
            {area.highlights?.length > 0 && (
              <div className="mt-10"><h3 className="font-semibold">Neighbourhoods we visit often</h3>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="area-highlights">{area.highlights.map((h) => <span key={h} className="inline-flex items-center gap-1 rounded-full border border-[#E5E2DA] bg-white px-3 py-1.5 text-sm"><MapPin className="w-3.5 h-3.5 text-[var(--kc-terracotta)]" />{h}</span>)}</div></div>
            )}
          </div>
          <div className="lg:col-span-5 space-y-6"><PostalCheck /><PriceTable title={`Standard Cleaning in ${area.city}`} packages={standardGeneral} testId="area-standard-pricing" /></div>
        </div>
      </section>

      {deepGeneral.length > 0 && <section className="kc-container pb-16"><PriceTable title={`Deep Cleaning in ${area.city}`} packages={deepGeneral} testId="area-deep-pricing" note="Detailed top-to-bottom cleaning, priced by home size." /></section>}

      {faqs.length > 0 && <section className="kc-section bg-white border-y border-[#E5E2DA]"><div className="kc-container"><SectionHeader eyebrow="FAQ" title={`Common questions from ${area.city}`} /><div className="mt-8"><FaqList faqs={faqs.slice(0, 4)} testPrefix="area-faq" /></div></div></section>}

      <section className="kc-section"><div className="kc-container"><SectionHeader eyebrow="Nearby" title="Other areas we serve" /><div className="mt-8"><ServiceAreasGrid areas={others} compact /></div></div></section>
      <CtaBand source={`area-${slug}-final`} title={`Ready for a cleaner home in ${area.city}?`} text={settings?.phone ? `Book online 24/7 or call ${settings.phone}.` : "Book online 24/7."} />
    </div>
  );
}
